import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText, FileDown, Truck, Loader2, AlertCircle,
  Inbox, X, ExternalLink, ChevronRight,
} from 'lucide-react';
import api from '../../services/api';
import { useMyDocumenti, usePagamentiDocumenti, MyDocumento } from '../../hooks/useMyDocumenti';
import { pdfErrorMessage, scaricaPdfDocumento } from '../../utils/pdfDocumento';
import { numeroDocumento } from '../../utils/numeroDocumento';
import { usePageSize } from '../../hooks/usePageSize';
import { VettoreLogo } from '../../components/portal/VettoreLogo';

/* ── Tipo documento — etichetta estesa ── */
const TYPE_LABELS: Record<string, string> = {
  fattura:      'Fattura',
  ddt:          'D.D.T.',
  nota_credito: 'Nota di credito',
};

const TYPE_BADGE: Record<string, string> = {
  fattura:      'text-emerald-700 bg-emerald-50 border-emerald-200',
  ddt:          'text-[hsl(210,64%,29%)] bg-[hsl(210,40%,95%)] border-[hsl(210,64%,80%)]',
  nota_credito: 'text-amber-700 bg-amber-50 border-amber-200',
};

const MESI = [
  'Gennaio','Febbraio','Marzo','Aprile','Maggio','Giugno',
  'Luglio','Agosto','Settembre','Ottobre','Novembre','Dicembre',
];

/* Il primo anno che la pagina offre: i Documenti si caricano dal gestionale
   un anno alla volta, dal corrente al 2021. */
const ANNO_MIN = 2021;
const PAGE_SIZES = [25, 50, 100];
const ANNO_CORRENTE = new Date().getFullYear();
const ANNI_DEFAULT = Array.from({ length: ANNO_CORRENTE - ANNO_MIN + 1 }, (_, i) => ANNO_CORRENTE - i);

/* Importo; null = Fattura differita di cui il gestionale non ha ancora dato
   il totale: uno skeleton, mai "€ 0,00". */
function Importo({ total, testId }: { total: number | null; testId?: string }) {
  if (total === null) {
    return <span data-testid={testId} title="Totale in arrivo dal gestionale"
      className="inline-block h-4 w-16 rounded bg-muted animate-pulse align-middle" />;
  }
  return <span data-testid={testId}>€ {total.toFixed(2).replace('.', ',')}</span>;
}

function dataOra(iso: string): string {
  const d = new Date(iso);
  return `${d.toLocaleDateString('it-IT')} alle ${d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}`;
}

/* ── Modale tracciamento spedizione ──
   Vettore e riferimento li dà il backend (``/spedizione``): il riferimento è
   quello che NTS scrive sul documento, il link è del sito del vettore. */
interface Spedizione { riferimento: string | null; vettore: string | null; url: string | null }

function TrackingModal({ doc, onClose }: { doc: MyDocumento; onClose: () => void }) {
  const [sped, setSped] = useState<Spedizione | null>(null);
  const [errore, setErrore] = useState<string | null>(null);

  React.useEffect(() => {
    const serie = encodeURIComponent(doc.key.serie || ' ');
    api.get<Spedizione>(`/api/clienti/me/documenti/${doc.key.tipork}/${doc.key.anno}/${serie}/${doc.key.numdoc}/spedizione`)
      .then(r => setSped(r.data))
      .catch(e => setErrore(e?.response?.data?.detail ?? 'Non riusciamo a leggere i dati della spedizione. Riprova più tardi.'));
  }, [doc]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.45)' }}
      onClick={e => e.target === e.currentTarget && onClose()}
      data-testid="tracking-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm overflow-hidden" role="dialog" aria-modal="true"
        aria-labelledby="tracking-title">
        <div className="bg-[hsl(210,64%,29%)] text-white px-5 py-3.5 flex items-center justify-between">
          <h2 id="tracking-title" className="font-semibold text-sm flex items-center gap-2">
            <Truck className="w-4 h-4" /> Segui la spedizione
          </h2>
          <button onClick={onClose} aria-label="Chiudi"
            className="w-7 h-7 rounded-full bg-white/20 hover:bg-white/35 flex items-center justify-center">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 space-y-3 text-sm">
          <div>
            <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">Documento</p>
            <p className="font-mono font-bold text-[hsl(210,64%,29%)]">{numeroDocumento(doc.numero)}</p>
          </div>
          {!sped && !errore && (
            <p className="flex items-center gap-2 text-muted-foreground" data-testid="tracking-loading">
              <Loader2 className="w-4 h-4 animate-spin" /> Recuperiamo i dati della spedizione…
            </p>
          )}
          {errore && (
            <p className="flex items-start gap-2 text-rose-700" data-testid="tracking-error">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /> {errore}
            </p>
          )}
          {sped && (
            <>
              <div>
                <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">Corriere</p>
                <p className="mt-0.5" data-testid="tracking-vettore">
                  {sped.vettore ? <VettoreLogo nome={sped.vettore} className="h-6" /> : 'Non indicato'}
                </p>
              </div>
              {sped.riferimento && (
                <div>
                  <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">Codice di tracciamento</p>
                  <p className="font-mono font-medium select-all" data-testid="tracking-riferimento">{sped.riferimento}</p>
                </div>
              )}
              {sped.url ? (
                <a href={sped.url} target="_blank" rel="noopener noreferrer" data-testid="tracking-link"
                  className="mt-2 w-full h-10 rounded-lg bg-[hsl(210,64%,29%)] text-white text-sm font-semibold inline-flex items-center justify-center gap-1.5 hover:bg-[hsl(210,64%,22%)] transition-colors">
                  <ExternalLink className="w-3.5 h-3.5" /> Segui su {sped.vettore}
                </a>
              ) : (
                <p className="text-xs text-muted-foreground" data-testid="tracking-no-link">
                  {sped.vettore
                    ? `Per ${sped.vettore} non abbiamo un collegamento diretto: usa il codice di tracciamento sul sito del corriere, o chiedi al tuo referente DTK.`
                    : 'Questa spedizione non risulta affidata a un corriere: per informazioni chiedi al tuo referente DTK.'}
                </p>
              )}
              <p className="text-[11px] text-muted-foreground">
                Il sito del corriere mostra la spedizione dopo il ritiro della merce, di solito dal giorno del documento.
              </p>
            </>
          )}
        </div>
        <div className="px-5 pb-4">
          <button onClick={onClose}
            className="w-full h-9 rounded-lg border text-sm font-medium hover:bg-[hsl(210,40%,97%)] transition-colors">
            Chiudi
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────── */
export default function DocumentsPage() {
  const navigate = useNavigate();
  const [tipoFilter, setTipoFilter] = useState('');
  const [annoFilter, setAnnoFilter] = useState<number>(ANNO_CORRENTE);
  const [meseFilter, setMeseFilter] = useState<number | ''>('');
  const [pageSize,   setPageSize]   = usePageSize('documenti', PAGE_SIZES);
  const [currentPage, setCurrentPage] = useState(1);
  const [trackingDoc, setTrackingDoc] = useState<MyDocumento | null>(null);
  const [downloadingKey, setDownloadingKey] = useState<string | null>(null);

  const { data, loading, error, reload } = useMyDocumenti({
    page: currentPage,
    pageSize,
    tipo: tipoFilter || undefined,
    anno: annoFilter,
    mese: meseFilter || undefined,
  });

  /* Download PDF dal bucket (URL presigned chiesto al clic) */
  const downloadPdf = async (doc: MyDocumento) => {
    const rowKey = `${doc.key.tipork}-${doc.key.anno}-${doc.key.numdoc}`;
    setDownloadingKey(rowKey);
    try {
      await scaricaPdfDocumento(doc.key);
    } catch (e: any) {
      alert(pdfErrorMessage(e));
    } finally {
      setDownloadingKey(null);
    }
  };

  /* Reset pagina quando cambiano i filtri */
  const resetPage = () => setCurrentPage(1);

  const goToDetail = (doc: MyDocumento) => {
    const serie = encodeURIComponent(doc.key.serie || ' ');
    navigate(`/area-clienti/documenti/${doc.key.tipork}/${doc.key.anno}/${serie}/${doc.key.numdoc}`);
  };

  const total      = data?.total ?? 0;
  const totalPages = data?.pages ?? 1;
  const pageItems  = data?.items ?? [];
  const pagamentoDi = usePagamentiDocumenti(pageItems);
  const availableAnni = data?.available_anni?.length ? data.available_anni : ANNI_DEFAULT;
  const caricamento = data?.caricamento ?? null;
  const annoInArrivo = caricamento?.stato === 'caricamento';
  const annoNonRaggiungibile = caricamento?.stato === 'errore';
  const filtriAttivi = !!tipoFilter || annoFilter !== ANNO_CORRENTE || !!meseFilter;

  return (
    <div data-testid="documents-page" className="space-y-5">

      {trackingDoc && (
        <TrackingModal doc={trackingDoc} onClose={() => setTrackingDoc(null)} />
      )}

      {/* ── Titolo ── */}
      <div>
        <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
          <FileText className="w-6 h-6 text-[hsl(210,64%,29%)]" />
          Documenti
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          DDT, fatture e note di credito dal gestionale, un anno alla volta.
        </p>
      </div>

      {/* ── Barra filtri ── */}
      <div className="flex flex-wrap items-center gap-3 bg-[hsl(210,40%,97%)] border rounded-xl px-4 py-3"
        data-testid="doc-filters">

        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-muted-foreground whitespace-nowrap">Tipologia documento:</span>
          <select value={tipoFilter}
            onChange={e => { setTipoFilter(e.target.value); resetPage(); }}
            data-testid="doc-type-filter"
            className="h-8 px-2.5 rounded-lg border bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(210,64%,29%)]/30">
            <option value="">Tutti</option>
            <option value="ddt">D.D.T.</option>
            <option value="fattura">Fattura</option>
            <option value="nota_credito">Nota di credito</option>
          </select>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-muted-foreground whitespace-nowrap">Anno:</span>
          <select value={annoFilter}
            onChange={e => { setAnnoFilter(Number(e.target.value)); resetPage(); }}
            data-testid="doc-anno-filter"
            className="h-8 px-2.5 rounded-lg border bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(210,64%,29%)]/30">
            {availableAnni.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-muted-foreground whitespace-nowrap">Periodo:</span>
          <select value={meseFilter}
            onChange={e => { setMeseFilter(e.target.value ? Number(e.target.value) : ''); resetPage(); }}
            data-testid="doc-mese-filter"
            className="h-8 px-2.5 rounded-lg border bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(210,64%,29%)]/30">
            <option value="">Tutti</option>
            {MESI.map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
          </select>
        </div>

        <div className="flex-1" />

        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-muted-foreground whitespace-nowrap">Visualizza:</span>
          <select value={pageSize}
            onChange={e => { setPageSize(Number(e.target.value)); resetPage(); }}
            data-testid="doc-pagesize"
            className="h-8 px-2.5 rounded-lg border bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(210,64%,29%)]/30">
            <option value={25}>25 documenti</option>
            <option value={50}>50 documenti</option>
            <option value={100}>100 documenti</option>
          </select>
        </div>

        {filtriAttivi && (
          <button onClick={() => { setTipoFilter(''); setAnnoFilter(ANNO_CORRENTE); setMeseFilter(''); resetPage(); }}
            data-testid="doc-filters-reset"
            className="h-8 px-3 rounded-lg border bg-white text-xs font-medium hover:bg-rose-50 hover:border-rose-300 hover:text-rose-700 transition-colors flex items-center gap-1">
            <X className="w-3 h-3" /> Reset
          </button>
        )}
      </div>

      {/* ── Errore ── */}
      {error && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800 flex items-center gap-2"
          data-testid="docs-error">
          <AlertCircle className="w-4 h-4" /> {error}
          <button onClick={reload} className="ml-auto underline text-xs">Riprova</button>
        </div>
      )}

      {/* ── Anno già in cache, ma l'ultimo aggiornamento non è riuscito ── */}
      {!loading && caricamento?.stato === 'pronto' && caricamento.ultimo_fallito && caricamento.aggiornato_il && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-xs text-amber-800 flex items-center gap-2"
          data-testid="docs-non-aggiornati">
          <AlertCircle className="w-4 h-4 shrink-0" />
          Gestionale non raggiungibile: documenti aggiornati il {dataOra(caricamento.aggiornato_il)}.
        </div>
      )}

      {/* ── Tabella ── */}
      <div className="rounded-xl border bg-white overflow-hidden shadow-sm">

        {/* Caricamento */}
        {loading && (
          <div className="p-12 flex flex-col items-center gap-2 text-muted-foreground"
            data-testid="docs-loading">
            <Loader2 className="w-6 h-6 animate-spin" />
            <span className="text-sm">Caricamento documenti…</span>
          </div>
        )}

        {/* L'anno sta arrivando dal gestionale: la pagina si aggiorna da sola */}
        {!loading && annoInArrivo && (
          <div className="p-12 flex flex-col items-center gap-2 text-center text-muted-foreground"
            data-testid="docs-caricamento-anno">
            <Loader2 className="w-6 h-6 animate-spin text-[hsl(210,64%,29%)]" />
            <p className="text-sm font-semibold text-foreground">Stiamo caricando i documenti del {annoFilter}</p>
            <p className="text-xs">La prima volta può servire fino a un minuto. La pagina si aggiorna da sola.</p>
          </div>
        )}

        {/* Mai caricato e il gestionale non risponde */}
        {!loading && annoNonRaggiungibile && (
          <div className="p-12 text-center" data-testid="docs-caricamento-errore">
            <AlertCircle className="w-10 h-10 text-rose-600 mx-auto mb-3" />
            <p className="text-sm font-semibold">Non riusciamo a caricare i documenti del {annoFilter}</p>
            <p className="text-xs text-muted-foreground mt-1">Il gestionale non risponde in questo momento.</p>
            <button onClick={reload} data-testid="docs-caricamento-riprova"
              className="mt-3 h-8 px-3 rounded-lg border bg-white text-xs font-medium hover:bg-[hsl(210,40%,97%)]">
              Riprova
            </button>
          </div>
        )}

        {/* Nessun documento */}
        {!loading && !annoInArrivo && !annoNonRaggiungibile && total === 0 && (
          <div className="p-12 text-center" data-testid="docs-empty">
            <Inbox className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-sm font-semibold">Nessun documento nel {annoFilter}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {tipoFilter || meseFilter
                ? 'Cambia i filtri per ampliare la ricerca.'
                : 'Scegli un altro anno per vedere i documenti precedenti.'}
            </p>
          </div>
        )}

        {/* Contenuto tabella */}
        {!loading && !annoInArrivo && total > 0 && (
          <>
            {/* Header colonne — desktop */}
            <div className="hidden md:grid bg-[hsl(210,64%,29%)] text-white text-[11px] font-semibold uppercase tracking-wide"
              style={{ gridTemplateColumns: '7.5rem 8.5rem 6rem 7.5rem 1fr 3.5rem 3.5rem 1.5rem' }}>
              <div className="px-4 py-2.5">Data</div>
              <div className="px-3 py-2.5">Tipo documento</div>
              <div className="px-3 py-2.5 text-center">Doc. n.</div>
              <div className="px-3 py-2.5 text-right">Importo</div>
              <div className="px-3 py-2.5">Modalità di pagamento</div>
              <div className="px-3 py-2.5 text-center">Scarica</div>
              <div className="px-3 py-2.5 text-center">Track.</div>
              <div className="px-1 py-2.5" />
            </div>

            {/* Righe */}
            <div data-testid="docs-table">
              {pageItems.map((doc, idx) => {
                const typeLabel  = TYPE_LABELS[doc.type] ?? TYPE_LABELS.altro;
                const badgeClass = TYPE_BADGE[doc.type]  ?? TYPE_BADGE.altro;
                const hasTracking = doc.tracciabile;
                const rowKey = `${doc.key.tipork}-${doc.key.anno}-${doc.key.serie}-${doc.key.numdoc}`;

                return (
                  <div key={rowKey}
                    data-testid={`doc-row-${doc.numdoc}`}
                    onClick={() => goToDetail(doc)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter') goToDetail(doc); }}
                    className={`border-b last:border-b-0 cursor-pointer
                      ${idx % 2 === 1 ? 'bg-[hsl(210,40%,99%)]' : 'bg-white'}
                      hover:bg-[hsl(210,40%,95%)] transition-colors
                      px-3 py-3 md:px-0 md:py-0`}>

                    {/* ── DESKTOP ── */}
                    <div className="hidden md:grid items-center w-full"
                      style={{ gridTemplateColumns: '7.5rem 8.5rem 6rem 7.5rem 1fr 3.5rem 3.5rem 1.5rem' }}>
                      <div className="px-4 py-3 text-sm tabular-nums text-muted-foreground">
                        {doc.date ? new Date(doc.date).toLocaleDateString('it-IT') : '—'}
                      </div>
                      <div className="px-3 py-3">
                        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded border ${badgeClass}`}>
                          {typeLabel}
                        </span>
                      </div>
                      <div className="px-3 py-3 text-center font-mono text-sm font-semibold text-[hsl(210,64%,29%)]">
                        {doc.numdoc}
                      </div>
                      <div className="px-3 py-3 text-right font-mono font-semibold text-sm tabular-nums">
                        <Importo total={doc.total} testId={`doc-total-${doc.numdoc}`} />
                      </div>
                      <div className="px-3 py-3 text-xs text-muted-foreground truncate"
                        title={pagamentoDi(doc) ?? undefined} data-testid={`doc-pagamento-${doc.numdoc}`}>
                        {pagamentoDi(doc) === undefined
                          ? <span className="inline-block h-3 w-24 rounded bg-muted animate-pulse align-middle" />
                          : (pagamentoDi(doc) ?? '—')}
                      </div>
                      <div className="flex items-center justify-center px-3 py-3">
                        {doc.hasPdf ? (
                          <button
                            onClick={(e) => { e.stopPropagation(); downloadPdf(doc); }}
                            disabled={downloadingKey === rowKey}
                            data-testid={`doc-download-${doc.numdoc}`}
                            title="Scarica PDF"
                            className="w-7 h-7 rounded flex items-center justify-center text-[hsl(210,64%,29%)] hover:bg-white transition-colors disabled:opacity-50">
                            {downloadingKey === rowKey
                              ? <Loader2 className="w-4 h-4 animate-spin" />
                              : <FileDown className="w-4 h-4" />}
                          </button>
                        ) : (
                          <button disabled title="PDF non disponibile"
                            data-testid={`doc-download-${doc.numdoc}`}
                            className="w-7 h-7 rounded flex items-center justify-center text-muted-foreground/30 cursor-not-allowed">
                            <FileDown className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      <div className="flex items-center justify-center px-3 py-3">
                        <button onClick={hasTracking ? (e) => { e.stopPropagation(); setTrackingDoc(doc); } : undefined}
                          disabled={!hasTracking}
                          title={hasTracking ? 'Segui la spedizione' : 'Nessuna spedizione da seguire'}
                          data-testid={`doc-tracking-${doc.numdoc}`}
                          className={`w-7 h-7 rounded flex items-center justify-center transition-colors ${
                            hasTracking
                              ? 'text-[hsl(210,64%,29%)] hover:bg-white'
                              : 'text-muted-foreground/30 cursor-not-allowed'
                          }`}>
                          <Truck className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="flex items-center justify-center px-1 py-3 text-muted-foreground/50">
                        <ChevronRight className="w-4 h-4" />
                      </div>
                    </div>
                    {/* ── MOBILE ── */}
                    <div className="md:hidden space-y-1.5">
                      <div className="flex items-center justify-between gap-2">
                        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded border ${badgeClass}`}>{typeLabel}</span>
                        <span className="text-xs text-muted-foreground">
                          {doc.date ? new Date(doc.date).toLocaleDateString('it-IT') : '—'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-mono text-sm font-semibold">n. {doc.numdoc}</span>
                        <span className="font-mono font-semibold text-sm"><Importo total={doc.total} /></span>
                      </div>
                      {pagamentoDi(doc) && <p className="text-xs text-muted-foreground truncate">{pagamentoDi(doc)}</p>}
                      {/* "Scarica" è l'azione primaria e prende la riga
                          intera; Tracking e Dettagli stanno sotto. I
                          pulsanti erano h-7 (28px). */}
                      <div className="pt-1 space-y-2">
                        <button
                          onClick={doc.hasPdf ? (e) => { e.stopPropagation(); downloadPdf(doc); } : undefined}
                          disabled={!doc.hasPdf || downloadingKey === rowKey}
                          className={`tap w-full h-11 px-3 rounded-lg border text-sm font-medium flex items-center justify-center gap-1.5 transition-colors ${
                            doc.hasPdf
                              ? 'text-[hsl(210,64%,29%)] hover:bg-[hsl(210,40%,95%)]'
                              : 'text-muted-foreground/40 cursor-not-allowed'
                          }`}>
                          {downloadingKey === rowKey
                            ? <Loader2 className="w-4 h-4 animate-spin" />
                            : <FileDown className="w-4 h-4" />}
                          Scarica
                        </button>
                        <div className="flex items-center gap-2">
                          {/* Un pulsante disabilitato senza spiegazione su
                              touch è un vicolo cieco: senza tracking si
                              mostra il perché, non un pulsante grigio. */}
                          {hasTracking ? (
                            <button
                              onClick={(e) => { e.stopPropagation(); setTrackingDoc(doc); }}
                              className="tap flex-1 h-11 px-3 rounded-lg border text-sm flex items-center justify-center gap-1.5 text-[hsl(210,64%,29%)] hover:bg-[hsl(210,40%,95%)] transition-colors">
                              <Truck className="w-4 h-4" /> Segui spedizione
                            </button>
                          ) : (
                            <span className="flex-1 text-[13px] text-muted-foreground">
                              Nessuna spedizione da seguire
                            </span>
                          )}
                          <span className="h-11 px-3 rounded-lg text-[13px] flex items-center gap-1 text-[hsl(210,64%,29%)]">
                            Dettagli <ChevronRight className="w-4 h-4" />
                          </span>
                        </div>
                      </div>
                    </div>

                  </div>
                );
              })}
            </div>

            {/* ── Paginazione ── */}
            {totalPages > 1 && (
              <div className="px-4 py-3 border-t bg-[hsl(210,40%,98%)] flex items-center gap-2 justify-between"
                data-testid="docs-pagination">
                <p className="text-xs text-muted-foreground">
                  {total} documenti ·{' '}
                  pagina <span className="font-semibold">{currentPage}</span> di <span className="font-semibold">{totalPages}</span>
                </p>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    data-testid="docs-pagination-prev"
                    className="h-7 w-7 rounded border text-xs flex items-center justify-center disabled:opacity-40 hover:bg-white transition-colors">
                    ‹
                  </button>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const start = Math.max(1, Math.min(currentPage - 2, totalPages - 4));
                    return start + i;
                  }).map(p => (
                    <button key={p}
                      onClick={() => setCurrentPage(p)}
                      className={`h-7 w-7 rounded border text-xs transition-colors ${
                        p === currentPage
                          ? 'bg-[hsl(210,64%,29%)] text-white border-[hsl(210,64%,29%)]'
                          : 'hover:bg-white'
                      }`}>
                      {p}
                    </button>
                  ))}
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    data-testid="docs-pagination-next"
                    className="h-7 w-7 rounded border text-xs flex items-center justify-center disabled:opacity-40 hover:bg-white transition-colors">
                    ›
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
